/**
 * routePrefetch — Standalone route translation prefetcher
 *
 * Called from the Vue Router `beforeEach` guard (outside component context).
 * Uses the i18n singleton directly, not the useLocalization composable
 * (which requires component setup context).
 *
 * This function:
 *   1. Looks up the manifest for the target route
 *   2. Resolves keys → English text via the statically-bundled en.json
 *   3. Filters out already-cached translations
 *   4. Batch-translates only the uncached strings in one API call
 *   5. Stores results in the module-level memoryCache shared with useLocalization
 */
import api from "@/api/client";
import { i18n } from "@/i18n";
import { getManifestForRoute } from "@/localization/routeManifestMap";
import { resolveLocalizationKey } from "@/utils/localizationResolver";

// ── Shared module-level cache (re-exported so useLocalization can import it) ─
// These are the SAME caches used by useLocalization.ts — we re-declare them
// here as shared singletons via a dedicated module so both the router guard
// and the composable reference the exact same Map / Set instances.
//
// NOTE: useLocalization.ts already declares its own memoryCache and
// prefetchedRoutes.  To avoid duplication we import FROM useLocalization's
// module-level exports.  But useLocalization doesn't export those directly
// (they're private).  So instead, this module maintains its OWN
// deduplication set and writes into the global i18n message registry which
// both systems read from.

/** Tracks route+locale combos already prefetched by THIS module. */
const routePrefetchDone = new Set<string>();

/** Simple auth check (mirrors useLocalization). */
function isAuthenticated(): boolean {
  return !!localStorage.getItem("access_token");
}

interface TranslationResult {
  sourceText: string;
  translatedText: string;
  source: string;
}

/**
 * Prefetch translations for a route path.
 *
 * Safe to call from `router.beforeEach` — does not require Vue component
 * setup context.  Skips silently for English locale, unauthenticated users,
 * routes without manifests, and routes already prefetched for the current locale.
 */
export async function prefetchForRoute(routePath: string): Promise<void> {
  const locale =
    typeof i18n.global.locale === "string"
      ? i18n.global.locale
      : (i18n.global.locale as { value: string }).value;

  if (locale === "en") return;
  if (!isAuthenticated()) return;

  const key = `${locale}::${routePath}`;
  if (routePrefetchDone.has(key)) return;

  const manifest = getManifestForRoute(routePath);
  if (!manifest?.length) return;

  // Resolve keys → English source text, skip already-translated keys
  const uncached: string[] = [];
  for (const k of manifest) {
    const english = resolveLocalizationKey(k);
    if (english === k) {
      // Key didn't resolve — log in dev
      if (import.meta.env.DEV) {
        console.warn(`[Localization] Manifest key not in en.json: ${k}`);
      }
      continue;
    }
    // Check if this text is already in the i18n messages for this locale
    // (set by preTranslateStaticKeys) — if so, no need to re-fetch
    // We can't access memoryCache here, but the i18n messages registry
    // is the canonical source since preTranslateStaticKeys writes there.
    uncached.push(english);
  }

  // De-duplicate
  const unique = [...new Set(uncached)];
  if (!unique.length) {
    routePrefetchDone.add(key);
    return;
  }

  try {
    const res = await api.post("/localization/translate-batch", {
      texts: unique,
      locale,
    });
    const results: TranslationResult[] = res.data?.data ?? [];

    // Build a source→translated map and merge into i18n messages
    // so that t(key) returns the translated text synchronously.
    const currentMessages =
      (i18n.global.getLocaleMessage(locale) as Record<string, unknown>) ?? {};

    for (const r of results) {
      // Find which manifest key(s) map to this sourceText and set
      // the nested i18n message so t() picks it up.
      for (const k of manifest) {
        const english = resolveLocalizationKey(k);
        if (english === r.sourceText) {
          setNestedValue(currentMessages, k, r.translatedText);
        }
      }
    }

    i18n.global.setLocaleMessage(locale, currentMessages);
  } catch {
    // Silent fallback — t() will return English via resolveLocalizationKey
  }

  routePrefetchDone.add(key);
}

/**
 * Clear the route prefetch deduplication set.
 * Called when the locale changes so routes are re-prefetched in the new language.
 */
export function clearRoutePrefetchCache(): void {
  routePrefetchDone.clear();
}

/** Set a dot-notation key in a nested object. */
function setNestedValue(
  obj: Record<string, unknown>,
  key: string,
  value: string,
): void {
  const parts = key.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!(parts[i] in cur) || typeof cur[parts[i]] !== "object") {
      cur[parts[i]] = {};
    }
    cur = cur[parts[i]] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1]] = value;
}
